<template>
    <teleport to="body">
        <Modal :is-open="show" :title="alertModalTitle">
            <template #header>
                <div class="flex gap-4 items-center">
                    <div
                        class="w-12 h-12 rounded-full flex flex-col justify-center items-center"
                        :class="{
                            'bg-red-50':
                                alertModalState.includes('step2.delete'),
                            'bg-slate-100 dark:bg-slate-700':
                                !alertModalState.includes('step2.delete'),
                        }"
                    >
                        <Icon
                            :icon="
                                alertModalState.includes('step2.delete')
                                    ? 'trash'
                                    : 'bell-outline'
                            "
                            :path-class="
                                alertModalState.includes('step2.delete')
                                    ? 'fill-red-500'
                                    : 'fill-slate-500'
                            "
                        />
                    </div>
                    <h5
                        id="modal-title"
                        class="text-slate-800 dark:text-slate-100 text-xl font-medium"
                    >
                        {{ alertModalTitle }}
                    </h5>
                </div>

                <button
                    class="z-10 w-10 rounded-lg h-12 justify-center items-center absolute right-6 top-6 focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200"
                    @click="closeModal"
                    tabindex="1"
                >
                    <icon
                        icon="x"
                        path-class="stroke-slate-500 hover:stroke-slate-700"
                        svg-class="fill-slate-500 hover:fill-slate-700 h-5 w-5"
                    />
                    <span class="sr-only">Close</span>
                </button>
            </template>
            <template #content>
                <!-- START STEP 1 PROPERTY SEARCH -->
                <div v-show="alertModalState === 'step1.create'" class="w-full">
                    <QuickSearch
                        type="hotels"
                        class="mb-5"
                        @propertyChanged="(v) => selectProperty(v)"
                    />
                </div>
                <!-- END STEP 1 PROPERTY SEARCH -->
                <!-- START PROPERTY DETAILS SECTION -->
                <section
                    v-if="
                        alertModalState.includes('step2.create') ||
                        alertModalState.includes('step2.edit') ||
                        alertModalState === 'step2.view'
                    "
                >
                    <div class="relative flex flex-col mb-8">
                        <div
                            class="property-container flex gap-4 flex-col sm:flex-row"
                        >
                            <!-- START IMAGE -->
                            <div
                                class="card-image relative sm:w-32 sm:max-w-32 rounded-lg overflow-hidden w-32 h-32"
                            >
                                <image-loader class="w-full h-full">
                                    <template #image>
                                        <img
                                            loading="lazy"
                                            :src="activeProperty.image_location"
                                            class="w-full h-full object-cover"
                                        />
                                    </template>
                                    <template #preloader>
                                        <div
                                            class="w-full sm:max-w-32 h-32 rounded-lg bg-slate-200 shimmer"
                                        ></div>
                                    </template>
                                    <template #error>
                                        <div
                                            class="w-full sm:max-w-32 h-32 rounded-lg object-cover flex items-center justify-center text-center flex-col bg-slate-200 text-slate-400"
                                        >
                                            <Icon icon="no-image" />
                                        </div>
                                    </template>
                                </image-loader>

                                <div
                                    class="absolute bottom-2 right-2 z-10 bg-white/90 rounded p-1"
                                >
                                    <img
                                        :src="`/images/${activeProperty?.program_image || activeProperty?.program.image}`"
                                        class="max-w-[48px] h-auto max-h-7"
                                    />
                                </div>
                            </div>
                            <!-- END IMAGE -->
                            <!-- START PROPERTY INFO CONTAINER -->
                            <div
                                class="flex flex-col flex-1 w-full justify-start"
                            >
                                <div
                                    class="flex-col justify-start items-start gap-2.5 flex"
                                    v-if="activeAlert?.uuid"
                                >
                                    <AlertStatusBadge :alert="activeAlert" />
                                </div>
                                <span
                                    class="cursor-pointer focus:outline-none focus:border-2 focus:border-slate-300 rounded-lg w-fit"
                                    role="link"
                                    tabindex="0"
                                    @click="navigateToProperty"
                                >
                                    <h4
                                        class="text-slate-800 dark:text-slate-100 text-base font-medium mb-1 pt-2 w-fit hover:text-slate-600"
                                    >
                                        {{ activeProperty?.name }}
                                    </h4>
                                    <address
                                        v-show="
                                            alertModalState !== 'step2.view'
                                        "
                                        class="text-xs md:text-sm text-slate-500 dark:text-slate-400 not-italic mb-3 hover:text-slate-400"
                                    >
                                        {{
                                            $filters.formatAddress(
                                                activeProperty
                                            )
                                        }}
                                    </address>
                                </span>
                                <p
                                    v-show="alertModalState !== 'step2.view'"
                                    class="text-xs md:text-sm text-gray-700 dark:text-slate-200"
                                >
                                    <a
                                        :href="activeProperty.url"
                                        v-if="activeProperty.url"
                                        class="cursor-pointer text-slate-500 hover:text-slate-600 text-sm outline-none underline"
                                        target="_blank"
                                    >
                                        Hotel Website
                                    </a>
                                </p>
                                <!-- START VIEW ALERT DETAILS (STEP2.VIEW only) -->
                                <ViewAlertDetails
                                    v-if="alertModalState === 'step2.view'"
                                    @edit="editAlert"
                                    @delete="() => transition('delete')"
                                />
                                <!-- END VIEW ALERT DETAILS -->
                            </div>
                        </div>
                    </div>
                    <!-- START EDIT ALERT CONTENTS (EDIT / CREATE STATES ONLY) -->
                    <div
                        class="w-full"
                        v-if="
                            alertModalState.includes('step2.edit') ||
                            alertModalState.includes('step2.create')
                        "
                    >
                        <ManageAlertForm
                            v-model="localValues"
                            :formErrors="formErrors"
                        />
                    </div>
                    <div
                        v-if="
                            alertModalState.includes('step2.edit') &&
                            activeAlert?.matches.length
                        "
                        class="mt-8"
                    >
                        <Alert
                            bgColor="amber"
                            iconColor="amber"
                            icon="warning"
                            title="Any matching availability will be deleted"
                            description="If you edit alert details and save the changes, any matching availability will be deleted."
                        />
                    </div>
                    <div class="modal-message p-3" v-if="errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                    <!-- START ALERT MATCHES: -->
                    <AlertMatches
                        :alert="activeAlert"
                        v-if="alertModalState === 'step2.view'"
                    />
                </section>
                <!-- END PROPERTY DETAILS SECTION -->
                <!-- START DELETE ALERT SECTION -->
                <p
                    class="text-slate-800 dark:text-slate-100 text-sm mb-5"
                    v-if="alertModalState.includes('step2.delete')"
                >
                    You are about to delete the alert. The search for available
                    rooms will stop and any results found will be deleted along
                    with the alert. This action cannot be undone.
                </p>
            </template>
            <template #footer v-if="alertModalState !== 'step2.view'">
                <div class="grid grid-cols-3">
                    <!-- START BACK BUTTON -->
                    <div class="col-span-1 flex justify-start">
                        <button
                            type="button"
                            class="px-4 py-2 text-slate-800 dark:text-slate-100 text-sm font-medium bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 justify-center items-center hover:border-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
                            v-if="alertModalState === 'step2.create'"
                            @click="() => transition('back')"
                        >
                            Back
                        </button>
                    </div>
                    <!-- END BACK BUTTON -->

                    <!-- START PROGRESS BAR (STEP1.CREATE and STEP2.CREATE ONLY)-->
                    <div
                        class="col-span-1 gap-2 flex items-center justify-center"
                    >
                        <div
                            v-show="
                                alertModalState === 'step1.create' ||
                                alertModalState === 'step2.create'
                            "
                            class="w-6 h-1 rounded-sm"
                            :class="
                                alertModalState === 'step1.create'
                                    ? 'bg-slate-800'
                                    : 'bg-slate-200'
                            "
                        ></div>
                        <div
                            v-show="
                                alertModalState === 'step1.create' ||
                                alertModalState === 'step2.create'
                            "
                            class="w-6 h-1 rounded-sm"
                            :class="
                                alertModalState === 'step2.create'
                                    ? 'bg-slate-800'
                                    : 'bg-slate-200'
                            "
                        ></div>
                    </div>
                    <!-- END PROGRESS BAR -->
                    <div class="col-span-1 flex justify-end items-center gap-4">
                        <!-- START CANCEL/NEXT BUTTONS -->
                        <button
                            type="button"
                            class="px-4 py-2 text-slate-800 dark:text-slate-100 text-sm font-medium bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 justify-center items-center hover:border-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
                            @click="handleCancel"
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            class="px-4 py-2 text-white text-sm font-medium bg-slate-800 shadow rounded justify-center border border-slate-800 hover:bg-slate-700 hover:border-slate-700 active:bg-slate-700 active:border-slate-700 focus:border-2 focus:border-slate-300"
                            :disabled="!localProperty"
                            @click="handleNext"
                            v-if="alertModalState === 'step1.create'"
                        >
                            Next
                        </button>
                        <!-- END CANCEL/NEXT BUTTONS -->

                        <!-- START SAVE OR CREATE BUTTON -->
                        <button
                            type="button"
                            class="px-4 py-2 text-white text-sm font-medium shadow rounded justify-center border focus:border-2 focus:outline-none"
                            :class="{
                                'bg-red-500 border-red-500 hover:bg-red-400 hover:border-red-400 active:bg-red-600 active:border-red-600 focus:border-red-300':
                                    alertModalState.includes('step2.delete'),
                                'bg-slate-800 border-slate-800 hover:bg-slate-700 hover:border-slate-700 active:bg-slate-700 active:border-slate-700 focus:border-slate-300 ':
                                    !alertModalState.includes('step2.delete'),
                            }"
                            @click="handleAlertAction"
                            v-if="localStep === 2 && localProperty"
                        >
                            {{ btnAction }}
                        </button>
                        <!-- END SAVE OR CREATE BUTTON -->
                    </div>
                </div>
            </template>
        </Modal>
    </teleport>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAlertsStore } from '@/store/alerts';
import { useRouter, useRoute } from 'vue-router';
import Alert from '@/components/ui/alert/ui-alert.vue';
import AlertMatches from '@/components/alerts/modals/alert-matches/alert-matches.vue';
import Modal from '@/components/ui/modal/ui-modal.vue';
import ManageAlertForm from '@/components/alerts/modals/manage-alert-form/manage-alert-form.vue';
import AlertStatusBadge from '@/components/alerts/alert-status-badge/alert-status-badge.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Errors from '@/components/errors.vue';
import QuickSearch from '@/components/hotels/search/quick-search.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import ViewAlertDetails from '@/components/alerts/modals/view-alert-details/view-alert-details.vue';
import dayjs from 'dayjs';
import Filters from '@/filters';

export default {
    props: {
        show: { type: Boolean, required: true },
        imageName: { type: String, default: 'alerts.svg' },
    },
    components: {
        Alert,
        AlertMatches,
        AlertStatusBadge,
        Modal,
        Errors,
        Icon,
        ManageAlertForm,
        QuickSearch,
        ImageLoader,
        ViewAlertDetails,
    },
    emits: ['close'],
    setup(_, { emit }) {
        const alertsStore = useAlertsStore();
        const { activeAlert, activeProperty, alertModalState } =
            storeToRefs(alertsStore);
        const {
            createAlert,
            deleteAlert,
            setActiveAlert,
            setActiveProperty,
            setAlertModalState,
            updateAlert,
        } = alertsStore;
        const router = useRouter();
        const errors = ref([]);
        const formErrors = ref({});
        const localStep = ref(1);
        const localProperty = ref(null);
        const localValues = ref({
            alertType: 'specific',
            checkIn: null,
            checkOut: null,
            numberOfNights: null,
            maxPoints: null,
            monthYear: null,
        });

        const states = {
            'step1.create': {
                next: 'step2.create',
                close: 'step1.create',
            },
            'step2.create': {
                back: 'step1.create',
                close: 'step1.create',
            },
            'step2.edit': {
                cancel: 'step2.view',
                close: 'step1.create',
            },
            'step2.view': {
                edit: 'step2.edit',
                close: 'step1.create',
                delete: 'step2.delete',
            },
            'step2.delete': {
                close: 'step1.create',
                cancel: 'step2.view',
            },
            // CREATE STATE (modal opened from property view)
            'step2.create.property': {
                close: 'step1.create',
            },
            // INDIE STATES (modal opened directly from alert item)
            'step2.edit.indie': {
                close: 'step1.create',
            },
            'step2.delete.indie': {
                close: 'step1.create',
            },
        };

        // COMPUTED:
        const alertModalTitle = computed(() => {
            switch (alertModalState.value) {
                case 'step1.create':
                case 'step2.create':
                case 'step2.create.property':
                    return 'Create Alert';
                case 'step2.edit':
                case 'step2.edit.indie':
                    return 'Edit Alert';
                case 'step2.view':
                    return 'Alert Details';
                case 'step2.delete':
                case 'step2.delete.indie':
                    return 'Delete Alert';
                default:
                    return 'Create Alert';
            }
        });

        const btnAction = computed(() => {
            if (alertModalState.value.includes('step2.create')) {
                return 'Create';
            } else if (alertModalState.value.includes('step2.edit')) {
                return 'Save Changes';
            } else if (alertModalState.value.includes('step2.delete')) {
                return 'Delete';
            }
        });

        // METHODS:

        watch(
            () => activeProperty.value,
            (newProperty) => {
                if (newProperty) {
                    localProperty.value = newProperty;
                }
            }
        );

        watch(
            () => alertModalState.value,
            (newState) => {
                if (newState.includes('step1')) {
                    localStep.value = 1;
                } else {
                    localStep.value = 2;
                }
            }
        );

        watch(
            () => localProperty.value,
            (newProperty) => {
                setActiveProperty(newProperty);
            }
        );

        watch(
            () => activeAlert.value,
            (newAlert) => {
                setAlertData(newAlert);
            },
            { immediate: true }
        );

        // METHODS:
        const transition = (action) => {
            const nextState = states[alertModalState.value][action];
            if (nextState) {
                setAlertModalState(nextState);
            } else {
                console.error(
                    `No transition for action ${action} from state ${alertModalState.value}`
                );
            }
        };

        function resetLocalValues() {
            localValues.value = {
                alertType: 'specific',
                checkIn: null,
                checkOut: null,
                numberOfNights: '',
                maxPoints: '',
                monthYear: null,
            };
        }

        function setAlertData(data) {
            if (data) {
                localValues.value = {
                    alertType: data.alert_type || 'specific',
                    checkIn: data.check_in || '',
                    checkOut: data.check_out || '',
                    numberOfNights: data.number_of_nights || null,
                    maxPoints: data.max_points || null,
                    monthYear: data.check_in
                        ? dayjs(data.check_in).format('MMMM YYYY')
                        : null,
                };
            }
        }

        function closeModal() {
            clearLocalData();
            setActiveProperty(null);
            setActiveAlert(null);
            emit('close');
            transition('close');
        }

        function resetFormErrors() {
            formErrors.value = {};
        }

        function clearLocalData() {
            resetLocalValues();
            resetFormErrors();
        }

        async function handleAlertAction() {
            if (alertModalState.value.includes('step2.create')) {
                handleCreate();
            } else if (alertModalState.value.includes('step2.edit')) {
                handleUpdate();
            } else if (alertModalState.value.includes('step2.delete')) {
                await deleteAlert(activeAlert.value);
                closeModal();
            }
        }

        async function handleCreate() {
            validate();

            let monthYear = localValues.value.monthYear;

            if (localValues.value.alertType === 'flexible') {
                monthYear = dayjs(localValues.value.monthYear).format(
                    'M/D/YYYY'
                );
                localValues.value.checkOut = null;
            }

            let data = {
                alert_type: localValues.value.alertType,
                check_in: localValues.value.checkIn,
                check_out: localValues.value.checkOut,
                number_of_nights: parseInt(localValues.value.numberOfNights),
                max_points: parseInt(localValues.value.maxPoints),
                month_year: monthYear,
                property_id: activeProperty.value.id,
            };

            if (!errors.value.length) {
                await createAlert(data, activeProperty.value.name);
                closeModal();
            }
        }

        async function handleUpdate() {
            validate();
            let data = {
                alert_type: localValues.value.alertType,
                check_in: localValues.value.checkIn,
                check_out: localValues.value.checkOut,
                number_of_nights: parseInt(localValues.value.numberOfNights),
                max_points: parseInt(localValues.value.maxPoints),
                month_year: localValues.value.monthYear,
                property_id: activeProperty.value.id,
            };
            if (!errors.value.length) {
                await updateAlert(data, activeAlert.value);
                closeModal();
            }
        }

        const handleCancel = () => {
            clearLocalData();
            if (
                alertModalState.value.includes('indie') ||
                alertModalState.value.includes('create')
            ) {
                closeModal();
            } else {
                transition('cancel');
            }
        };
        function handleNext() {
            setActiveProperty(localProperty.value);
            localStep.value = 2;
            transition('next');
        }

        function validate() {
            formErrors.value = {};
            errors.value = [];

            if (!localProperty.value) {
                errors.value.push({ message: 'Please choose a property.' });
            }

            if (localValues.value.alertType === 'specific') {
                if (!localValues.value.checkIn) {
                    formErrors.value.start_date =
                        'Please choose a check in date.';
                }
                if (!localValues.value.checkOut) {
                    formErrors.value.end_date =
                        'Please choose a check out date.';
                }
                if (!localValues.value.checkIn || !localValues.value.checkOut) {
                    errors.value.push({
                        message: 'Please choose start and end dates.',
                    });
                }
                if (localValues.value.checkIn && localValues.value.checkOut) {
                    validateDateRange();
                }
            }

            if (localValues.value.alertType === 'flexible') {
                if (!localValues.value.monthYear) {
                    formErrors.value.month_year =
                        'Please choose a month and year.';
                    this.errors.push({
                        message: 'Please choose a month and year.',
                    });
                }
                if (!localValues.value.numberOfNights) {
                    formErrors.value.nights =
                        'Please enter the number of nights.';
                    errors.value.push({
                        message: 'Please enter the number of nights.',
                    });
                }
            }
        }

        function editAlert() {
            setAlertData(activeAlert.value);
            transition('edit');
        }
        function selectProperty(v) {
            localProperty.value = v;
            setActiveProperty(v);
        }

        function validateDateRange() {
            if (
                !Filters.isValidDateRange(
                    localValues.value.checkIn,
                    localValues.value.checkOut
                )
            ) {
                formErrors.value.end_date =
                    'Check-out date cannot be before check-in date.';
                errors.value.push({
                    message: 'Check-out date cannot be before check-in date.',
                });
                localValues.value.checkOut = null;
            }
        }
        function navigateToProperty() {
            router.push(`/hotels/properties/${activeProperty.value?.slug}`);
            closeModal();
        }

        return {
            activeAlert,
            activeProperty,
            alertModalState,
            alertModalTitle,
            btnAction,
            closeModal,
            editAlert,
            errors,
            formErrors,
            handleAlertAction,
            handleCancel,
            handleNext,
            localProperty,
            localStep,
            localValues,
            navigateToProperty,
            selectProperty,
            transition,
        };
    },
};
</script>
